import {
  Marketplace,
  PosFieldArrayOfTag,
  Tag,
  TagsValueType,
} from 'src/WebApiController';

/** Minimal shape shared by the full `Tag` and the report-only `ReportTag`. */
export type TagColumnValue = {
  key: string;
  value: string;
  valueType?: TagsValueType | null;
};

/**
 * Numeric tags keep showing only their first value in columns so that
 * number formatting/aggregation is not broken.
 */
export const isNumericTagValueType = (
  valueType: TagsValueType | null | undefined
): boolean =>
  valueType === TagsValueType.Int || valueType === TagsValueType.Decimal;

/**
 * Returns every tag that matches the given key, preserving order.
 * Empty values are kept so the first match is identical to the legacy
 * `tags.find((t) => t.key === key)` behavior.
 */
export const getTagValuesForKey = <T extends TagColumnValue>(
  tags: T[] | null | undefined,
  key: string
): T[] => (tags ?? []).filter((t) => t.key === key);

const getNonEmptyValues = <T extends TagColumnValue>(
  tags: T[] | null | undefined,
  key: string
): string[] =>
  getTagValuesForKey(tags, key)
    .map((t) => t.value)
    .filter((value) => Boolean(value));

/**
 * Resolves the value used by a numeric-capable tag column accessor
 * (sorting/export/display).
 *
 * - Numeric tags (Int/Decimal) always return only the first value.
 * - When `hasMultiValueTagsInColumns` is enabled, non-numeric tags return all their
 *   (non-empty) values joined with ", ".
 * - Otherwise the first value is returned (pre-feature behavior).
 */
export const getTagColumnAccessorValue = <T extends TagColumnValue>(
  tags: T[] | null | undefined,
  key: string,
  hasMultiValueTagsInColumns: boolean
): string | number | null => {
  const firstTag = getTagValuesForKey(tags, key)[0];

  if (!firstTag) {
    return null;
  }

  if (isNumericTagValueType(firstTag.valueType)) {
    return firstTag.value ? parseFloat(firstTag.value) : null;
  }

  if (hasMultiValueTagsInColumns) {
    const values = getNonEmptyValues(tags, key);
    if (values.length > 1) {
      return values.join(', ');
    }
  }

  return firstTag.value;
};

/**
 * Resolves the string value used by a string-only tag column accessor
 * (e.g. report event-tag columns). Numeric values are returned as-is.
 */
export const getTagStringColumnAccessorValue = <T extends TagColumnValue>(
  tags: T[] | null | undefined,
  key: string,
  hasMultiValueTagsInColumns: boolean
): string | null => {
  const firstTag = getTagValuesForKey(tags, key)[0];

  if (!firstTag) {
    return null;
  }

  if (hasMultiValueTagsInColumns) {
    const values = getNonEmptyValues(tags, key);
    if (values.length > 1) {
      return values.join(', ');
    }
  }

  return firstTag.value;
};

export const tagFieldHasChanges = (
  tags1: PosFieldArrayOfTag | null | undefined,
  tags2: PosFieldArrayOfTag | null | undefined,
  // tagsMetadata is an optional parameter
  // If it is provided, only tags that are in tagsMetadata will be compared
  tagsMetadata?: Tag[]
): boolean => {
  if (!tags1?.hasChanged) {
    return false;
  }

  const _tags1 = !tags1?.value
    ? null
    : !tagsMetadata
      ? tags1.value
      : tags1.value.filter((t) => tagsMetadata.find((tm) => tm.key === t.key));
  const _tags2 = !tags2?.value
    ? null
    : !tagsMetadata
      ? tags2.value
      : tags2.value.filter((t) => tagsMetadata.find((tm) => tm.key === t.key));

  if ((!_tags1 || !_tags1.length) && (!_tags2 || !_tags2.length)) {
    return false;
  }
  if (!_tags1 || !_tags1.length || !_tags2 || !_tags2.length) {
    return true;
  }
  if (_tags1.length !== _tags2.length) {
    return true;
  }
  return _tags1.some((t1) => {
    return !_tags2.find(
      (t2) =>
        t1.key === t2.key &&
        t1.value === t2.value &&
        t1.includeInReport === t2.includeInReport &&
        t1.ordinal === t2.ordinal
    );
  });
};

export const tagHasChanges = (
  tags1: Tag[] | null | undefined,
  tags2: Tag[] | null | undefined,
  // tagsMetadata is an optional parameter
  // If it is provided, only tags that are in tagsMetadata will be compared
  tagsMetadata?: Tag[]
): boolean => {
  const _tags1 = !tags1
    ? null
    : !tagsMetadata
      ? tags1
      : tags1.filter((t) => tagsMetadata.find((tm) => tm.key === t.key));
  const _tags2 = !tags2
    ? null
    : !tagsMetadata
      ? tags2
      : tags2.filter((t) => tagsMetadata.find((tm) => tm.key === t.key));

  if ((!_tags1 || !_tags1.length) && (!_tags2 || !_tags2.length)) {
    return false;
  }
  if (!_tags1 || !_tags1.length || !_tags2 || !_tags2.length) {
    return true;
  }
  if (_tags1.length !== _tags2.length) {
    return true;
  }
  return _tags1.some((t1) => {
    return !_tags2.find(
      (t2) =>
        t1.key === t2.key &&
        t1.value === t2.value &&
        t1.includeInReport === t2.includeInReport &&
        t1.ordinal === t2.ordinal
    );
  });
};

export function ToMarketplaceEnum(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const entry = Object.entries(Marketplace).find(([, val]) => val === value);
  return entry ? entry[1] : undefined;
}
